import { Address, useLoadAllItemsQuery } from '@sequencehq/api'
import {
  Dashboardv99990101Api,
  dashboardv99990101Client
} from '@sequencehq/api/dist/clients/dashboard/v99990101'
import { useUpdateCustomer } from 'Customer/index'
import { useCubeContext } from 'modules/Cube/communication/internal/cube.domain.context'
import {
  Common,
  Customer,
  ValidationResult
} from 'modules/Cube/domain/cube.domain.types'
import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useExtractedCustomer,
  type ExtractedCustomer
} from './useExtractedCustomer.connector'
import { useMarkExtractedFieldsAsAccepted } from 'modules/ContractExtraction'
import { useDismissExtractedFields } from 'ContractExtraction/hooks/useDismissExtractedFields'
import { contactFromEmail } from 'CustomerContacts/index'
import { CustomerContact } from 'CustomerContacts/domain/CustomerContacts.domain.types'
import { setPrimaryBillingContact } from 'CustomerContacts/utils/setPrimaryBillingContact'
import { PerformCreateCustomer } from 'components/Customers/hooks/useCreateCustomer'

type CustomerObject = {
  label: Customer['legalName']
  value: Customer['id']
}

type CustomerSelectionField = {
  options: CustomerObject[]
  value: Customer['id']
  onChange: (newValue: Customer['id']) => void
  disabled: boolean
  validationErrors: ValidationResult[]
  onAddNew: () => void
}
export type WidgetView = 'customerSelection' | 'customerView'

type UseCustomerWidget = () => {
  customer?: Customer
  showEditButton: boolean
  addCustomerDisabled: boolean
  view: WidgetView
  updateView: (newView: WidgetView) => void
  fieldConfig: {
    customer: CustomerSelectionField
    rollUpBilling: {
      hidden: boolean
      isDisabled: boolean
      validationErrors: ValidationResult[]
      value: boolean
      onChange: (value: boolean) => void
      childAccounts: string[]
      childAccountCount: number
    }
  }
  isLoading: boolean
  updateCustomer: () => Promise<void>
  modifiedCustomer: ExtractedCustomer
  ignoreCustomerChanges: () => void
  createCustomer: PerformCreateCustomer
}

export const useCustomerWidget: UseCustomerWidget = () => {
  const navigate = useNavigate()

  const cubeContext = useCubeContext()
  const customerId = cubeContext.queries.rawData.data.common.customerId
  const customer = cubeContext.queries.rawData.data.customers[customerId]

  const [view, setView] = useState<'customerSelection' | 'customerView'>(
    customer ? 'customerView' : 'customerSelection'
  )

  const customersQuery = useLoadAllItemsQuery(
    dashboardv99990101Client.getCustomers
  )

  const customerOptions = useMemo(() => {
    if (!customersQuery.data) {
      return []
    }

    return customersQuery.data.pages
      .flatMap(customersPage => customersPage.items)
      .map(availableCustomer => ({
        label: availableCustomer.legalName,
        value: availableCustomer.id
      }))
      .sort((a, b) =>
        a.label?.toUpperCase() < b.label?.toUpperCase() ? -1 : 1
      )
  }, [customersQuery.data])

  const loadFullCustomer = useCallback(
    async (newCustomerId: string) => {
      const fullCustomer =
        (await cubeContext.mutators.external.in.customer({
          customerId: newCustomerId
        })) ?? {}

      cubeContext.mutators.updateData({
        customers: {
          [newCustomerId]: fullCustomer
        }
      })
      return
    },
    [cubeContext.mutators]
  )

  const changeSelectedCustomer = useCallback(
    async (newCustomerId: Common['customerId']) => {
      await loadFullCustomer(newCustomerId).catch(() => {
        console.error(`Failed to load full customer with id ${newCustomerId}`)
      })

      await cubeContext.mutators.updateData({
        common: {
          customerId: newCustomerId
        }
      })

      setView('customerView')
    },
    [cubeContext, loadFullCustomer]
  )

  const extractedCustomer = useExtractedCustomer(customer)
  const markExtractedFieldsAsAccepted = useMarkExtractedFieldsAsAccepted()
  const dismissExtractedFields = useDismissExtractedFields()

  const showEditButton =
    cubeContext.queries.availableFeatures.common.editCustomer.available
      .visible && view !== 'customerSelection'

  const updateView = useCallback((newView: WidgetView) => {
    return setView(newView)
  }, [])

  const childAccounts = useMemo(() => {
    return customer?.organizations?.some(
      org => org?.owner?.id === customerId && org?.members?.length !== 0
    )
      ? customer?.organizations?.flatMap(
          org => org?.members?.map(member => member?.name) ?? []
        ) ?? []
      : customer?.aliases
          ?.map(alias => alias.label || alias.value)
          .sort((a, b) => (a.toLowerCase() > b.toLowerCase() ? 1 : -1)) ?? []
  }, [customer?.aliases, customer?.organizations, customerId])

  const { updateCustomer } = useUpdateCustomer(customer?.id ?? '')
  const updateCustomerAndCube = useCallback(
    async (
      updatedCustomer: Partial<Dashboardv99990101Api.PutCustomer.UpdateCustomerBody>
    ) => {
      const res = await updateCustomer(updatedCustomer)

      markExtractedFieldsAsAccepted(['customer.*'])

      if (res) {
        cubeContext.mutators.updateData({
          customers: {
            [customer?.id ?? '']: res
          }
        })
      }

      return res
    },
    [
      cubeContext.mutators,
      customer?.id,
      updateCustomer,
      markExtractedFieldsAsAccepted
    ]
  )

  const handleUpdateCustomer = useCallback(async () => {
    const payload: { address?: Address; contacts?: CustomerContact[] } = {}

    if (extractedCustomer.address) {
      payload.address = extractedCustomer.address
    }

    if (extractedCustomer.billingEmail) {
      const newContact = contactFromEmail(
        extractedCustomer.billingEmail,
        'PRIMARY'
      )
      const newContacts = [...(customer?.contacts ?? []), newContact]

      payload.contacts = setPrimaryBillingContact(
        newContacts,
        contact => contact.email === newContact.email
      )
    }

    if (!Object.keys(payload).length) {
      return
    }

    await updateCustomerAndCube(payload)
  }, [updateCustomerAndCube, extractedCustomer, customer?.contacts])

  const handleCreateCustomer = useCallback<PerformCreateCustomer>(
    async ({ body }) => {
      // a "create" in this case is actually an update from status DRAFT to ACTIVE
      const result = await updateCustomerAndCube({
        ...body,
        status: 'ACTIVE'
      })

      if (!result) {
        throw new Error('Failed to create customer')
      }

      return result
    },
    [updateCustomerAndCube]
  )

  const fieldConfig = useMemo(
    () => ({
      customer: {
        disabled: false,
        value: cubeContext.queries.rawData.data.common.customerId,
        onChange: changeSelectedCustomer,
        options: customerOptions,
        validationErrors:
          cubeContext.queries.validation.activeValidationResults?.common
            .customerId ?? [],
        onAddNew: () => {
          setView('customerView')
          navigate('./customers/new')
        }
      },
      rollUpBilling: {
        hidden:
          !cubeContext.queries.availableFeatures.schedule.rollUpBilling
            .available.visible,
        isDisabled:
          !cubeContext.queries.availableFeatures.schedule.rollUpBilling
            .available.enabled,
        validationErrors: [],
        onChange: (value: boolean) => {
          cubeContext.mutators.updateData({
            schedule: {
              rollUpBilling: value
            }
          })
        },
        value: cubeContext.queries.rawData.data.schedule.rollUpBilling,
        childAccounts,
        childAccountCount: childAccounts.length
      }
    }),
    [
      cubeContext.queries.rawData.data.common.customerId,
      cubeContext.queries.rawData.data.schedule.rollUpBilling,
      cubeContext.queries.validation.activeValidationResults?.common.customerId,
      cubeContext.queries.availableFeatures.schedule.rollUpBilling.available
        .visible,
      cubeContext.queries.availableFeatures.schedule.rollUpBilling.available
        .enabled,
      cubeContext.mutators,
      changeSelectedCustomer,
      customerOptions,
      childAccounts,
      navigate
    ]
  )

  const ignoreCustomerChanges = useCallback(() => {
    dismissExtractedFields(['customer.*'])
    setView('customerView')
  }, [dismissExtractedFields])

  return {
    customer,
    isLoading: customersQuery.isPending,
    showEditButton,
    addCustomerDisabled:
      !cubeContext.queries.availableFeatures.common.editCustomer.available
        .enabled,
    view,
    createCustomer: handleCreateCustomer,
    updateView,
    fieldConfig,
    updateCustomer: handleUpdateCustomer,
    ignoreCustomerChanges,
    modifiedCustomer: extractedCustomer
  }
}
